import React, { useEffect } from 'react'
import IconedCallout from 'components/Common/IconedCallout'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import PaneHeader from 'components/Common/Pane/PaneHeader'
import ImageHero from 'components/Common/ImageHero'
import HeadData from 'components/Common/HeadData'
import { OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_CRUISE_SLUG, OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { connect } from 'react-redux'
import {
  fetchLandingPage,
} from 'actions/ContentActions'
import { TYPE_OF_PAGE_BNBL, PAGE_NAME_BNBL_LANDING } from 'constants/landingPage'
import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import ErrorPage from 'components/Pages/ErrorPage'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LayoutContainer from 'components/Common/LayoutContainer'
import Faq from 'components/Common/Faq'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getContentPageKey } from 'lib/url/contentUtils'
import OfferListHeading from 'components/OfferList/OfferListHeading'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import List from 'components/Common/List'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'

const EmpowermentTickIcon = styled(LineCheckIcon)`
  position: relative;
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

const SameHeightPaneSm = styled(Pane)`
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    height: 100%;
  }
`

const StyledList = styled(List)`
  margin-top: 0;
  margin-bottom: 0;
`

const StyledPaneBody = styled(PaneBody)`
  padding-top: 0;
  margin-top: 0;
`

const ThreeColumnGrid = styled.div`
  display: grid;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const HOTEL_REDIRECT = `/${OFFER_TYPE_HOTEL_SLUG}`
const TOUR_EXCLUDED = [`!${OFFER_TYPE_TOUR_SLUG}`, `!${OFFER_TYPE_CRUISE_SLUG}`]

const offerListFilters: App.OfferListFilters = {
  holidayTypes: TOUR_EXCLUDED,
  offerTypes: FLASH_OFFERS_TYPES,
  frontPageVisibleOnly: true,
}

interface Props {
  content: App.ContentBNBLPage
  error?: any
}

function BNBLLandingPage(props: Props) {
  const { content, error } = props
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchLandingPage(PAGE_NAME_BNBL_LANDING, TYPE_OF_PAGE_BNBL))
  }, [dispatch])

  if (error) {
    if (error.status == 404) {
      return (
        <HttpStatus code={404}>
          <Error404PageWithOffers />
        </HttpStatus>
      )
    } else {
      return <ErrorPage error={error} />
    }
  }

  if (!content) {
    return <LoadingIndicator />
  }

  const {
    header,
    empowerment,
    howItWorks,
    faq,
    offerList,
  } = content

  const offerListExtras: Array<OfferListV2Item> = [
    {
      id: 'heading',
      position: 'start',
      element: <OfferListHeading title={offerList.title} />,
      show: 'hasOffers',
    },
    {
      id: 'bnbl_discover_deals',
      position: 'end',
      element: (
        <Group direction="horizontal" horizontalAlign="center">
          <TextButton kind="primary" to={HOTEL_REDIRECT}>
            {offerList.btnText}
          </TextButton>
        </Group>
      ),
      show: 'always',
    },
  ]

  return (
    <>
      <HeadData title={header.metaTitle || header.title} description={header.metaDescription || header.subTitle} />
      <ImageHero
        alt={header.title}
        aria-hidden="true"
        brightness={-15}
        gravity="auto"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="3:1"
        id={header.image}
      >
        <LayoutContainer size="md">
          <Heading align="center" variant="heading2" as="h1" colour="neutral-eight">
            {header.title}
          </Heading>
          <BodyText variant="large" align="center" as="h2" colour="neutral-eight">
            {header.subTitle}
          </BodyText>
        </LayoutContainer>
      </ImageHero>
      <Pane>
        <PaneHeader size="xl">
          <Heading variant="heading2" align="center" as="h2">
            {empowerment.title}
          </Heading>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer size="sm">
            <StyledList type="unstyled" spaced>
              {empowerment.description.map(listItem => (
                <li key={listItem}>
                  <Group direction="horizontal" gap={16}>
                    <div>
                      <EmpowermentTickIcon size={26} colour="primary" />
                    </div>
                    {listItem}
                  </Group>
                </li>
              ))}
            </StyledList>
          </LayoutContainer>
        </StyledPaneBody>
      </Pane>
      <Pane background="neutral-six">
        <PaneHeader size="xl-y md">
          <Heading variant="heading3" align="center" as="h2">
            {howItWorks.title}
          </Heading>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer>
            <ThreeColumnGrid>
              {howItWorks.description.map(item => (
                <div key={item.title}>
                  <SameHeightPaneSm>
                    <PaneBody size="lg xl-y">
                      <IconedCallout
                        icon={
                          <img
                            src={item.icon.url}
                            width={70}
                            height={70}
                            alt={item.icon.title}
                          >
                          </img>
                        }
                        title={item.title}
                        text={item.text}
                      />
                    </PaneBody>
                  </SameHeightPaneSm>
                </div>
              ))}
            </ThreeColumnGrid>
          </LayoutContainer>
        </StyledPaneBody>
      </Pane>
      {faq && <Pane>
        <PaneHeader size="xl-y md">
          <Heading variant="heading3" align="center" as="h2">
            {faq.title}
          </Heading>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer>
            <Faq slug={faq.slug} />
          </LayoutContainer>
        </StyledPaneBody>
      </Pane>}
      {offerList && <Pane background="neutral-six">
        <PaneBody size="lg">
          <LayoutContainer>
            <OfferList
              maxOffers={offerList.numberOfOffers}
              interstitials={offerListExtras}
              filters={offerListFilters}
              random
            />
          </LayoutContainer>
        </PaneBody>
      </Pane>}
    </>
  )
}

const key = getContentPageKey(PAGE_NAME_BNBL_LANDING, TYPE_OF_PAGE_BNBL)
function mapStateToProps(state: App.State) {
  return {
    content: state.content.landingPages[key],
    error: state.content.landingPageErrors[key],
  }
}

export default connect(
  mapStateToProps,
)(BNBLLandingPage)
